//  Copyright (c) 2019 First Foundry Inc. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { withVenueID, withVenueSettings } from 'components/Venue'
import { Form } from 'react-final-form'
import Spinner from 'components/common/display/Spinner'
import ReportHeader from 'components/pages/reports/common/ReportHeader'
import { withQueryErrorPageOnError } from 'components/pages/ErrorPage'
import { renderWhileLoading } from 'utils/hoc'
import dateDifference from 'utils/dateDifference'
import { compose, withProps, withState } from 'recompose'
import { DEFAULT_TIMEZONE } from 'constants/Settings'
import moment from 'moment-timezone'
import PriceGroupLogsTable from './PriceGroupLogsTable'
import withPriceGroupLogs from './withPriceGroupLogs'

const PriceGroupLogs = ({
  packageChangeLogData,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  venueSettings,
}) => (
  <Form
    initialValues={{
      startTime,
      endTime,
    }}
    onSubmit={(values) => {
      setStartTime(values.startTime)
      setEndTime(values.endTime)
    }}
    render={({ handleSubmit, values, form }) => {
      const leftArrowClick = (name) => {
        form.change(name, new Date(values[name].setDate(values[name].getDate() - 1)))
      }
      const rightArrowClick = (name) => {
        form.change(name, new Date(values[name].setDate(values[name].getDate() + 1)))
      }

      // group the data by date and employeeID
      const pkgLog = get(packageChangeLogData, 'node.changelog.edges')
      const pkgLogArray = []
      const pkgLogDates = []

      pkgLog.forEach((item) => {
        const logDate = get(item, 'node.timestamp').match(/(\d+-\d+-\d+T[0-1]?[0-9]|2[0-3]):[0-5][0-9]/)[0]
        pkgLogDates.push(logDate)
        if (pkgLogDates.indexOf(logDate) >= -1) {
          pkgLogDates.push(logDate)
        }
        pkgLogArray.push({
          id: get(item, 'node.id'),
          employeeId: get(item, 'node.employeeId'),
          employeeName: get(item, 'node.employeeName'),
          logDate,
          timestamp: get(item, 'node.timestamp'),
          payload: get(item, 'node.payload'),
        })
      })
      // join related changes based on employeeID and Date
      const multipleGroupByArray = ((dataArray, groupPropertyArray) => {
        const groups = {}
        dataArray.forEach((item) => {
            const group = JSON.stringify(groupPropertyArray(item))
            groups[group] = groups[group] || []
            groups[group].push(item)
        })
        return Object.keys(groups).map(group => groups[group])
      })

      const groupedPkgLog = multipleGroupByArray(
        pkgLogArray, property => [property.logDate, property.employeeId],
      )
      const mappedPkgLog = []
      groupedPkgLog.forEach((item) => {
        const changes = []
        let employee = ''
        let empName = ''
        let logTime = ''
        item.forEach((logItem) => {
          employee = logItem.employeeId
          empName = logItem.employeeName
          logTime = logItem.timestamp
          changes.push(logItem.payload)
        })
        mappedPkgLog.push({
          employeeId: employee,
          employeeName: empName,
          timestamp: logTime,
          payload: changes,
        })
      })
      const dateRange = dateDifference(values.startTime, new Date())
      return (
        <form onSubmit={handleSubmit}>
          <ReportHeader
            disabled={dateRange > 30 || values.startTime > values.endTime}
            startTime={startTime}
            endTime={endTime}
            leftArrowClick={leftArrowClick}
            rightArrowClick={rightArrowClick}
          />
          <PriceGroupLogsTable
            storeTimezone={get(venueSettings, 'store.timezone', DEFAULT_TIMEZONE)}
            startDate={values.startTime}
            endDate={values.endTime}
            data={mappedPkgLog}
          />
        </form>
      )
    }}
  />
)

PriceGroupLogs.propTypes = {
  startTime: PropTypes.object,
  endTime: PropTypes.object,
  packageChangeLogData: PropTypes.object,
  setStartTime: PropTypes.func,
  setEndTime: PropTypes.func,
  venueSettings: PropTypes.object,
}
const dt = new Date()
dt.setDate(dt.getDate() - 30)

export default compose(
  withVenueID,
  withVenueSettings({ name: 'venueSettings' }),
  withState('startTime', 'setStartTime', moment.tz(new Date().setDate(new Date().getDate() - 7), 'America/Los_Angeles').toDate()),
  withState('endTime', 'setEndTime', moment.tz(new Date(), 'America/Los_Angeles').toDate()),
  withProps(props => ({
    startTime: dt,
    endTime: new Date(),
    ...props,
  })),
  withPriceGroupLogs,
  renderWhileLoading(
    () => <Spinner wrapStyle={{ paddingTop: '100px' }} />,
    ['packageChangeLogData'],
  ),
  withQueryErrorPageOnError(['packageChangeLogData'], true),
)(PriceGroupLogs)
